import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

type Props = HTMLAttributes<HTMLDivElement> & {
  padded?: boolean;
  glow?: boolean;
  /**
   * Card as a whole is clickable (wraps a link/form/button — W6 hover-polish).
   * Adds a subtle border-lighten hover. NEVER set this on a purely
   * static/informational card (false-affordance).
   */
  interactive?: boolean;
  style?: CSSProperties;
  children: ReactNode;
};

/**
 * DesignCard — sport-grit card. Renamed from the design's `Card` to avoid
 * collision with the existing `components/ui/Card.tsx`. Uses surface-1 +
 * border + bm-grit scan-line texture.
 */
export function DesignCard({
  padded = true,
  glow = false,
  interactive = false,
  style,
  className = "",
  children,
  ...rest
}: Props) {
  return (
    <div
      className={`bm-grit ${
        interactive
          ? "border border-[var(--color-border)] hover:border-[var(--color-border-strong)] transition-colors duration-150 motion-reduce:transition-none"
          : ""
      } ${className}`}
      style={{
        background: "var(--color-surface-1)",
        // interactive: border comes from the className above (inline style
        // would win specificity and mute the hover variant), otherwise the
        // long-standing static border.
        border: interactive ? undefined : "1px solid var(--color-border)",
        borderRadius: "var(--radius-lg)",
        padding: padded ? 16 : 0,
        boxShadow: glow
          ? "0 0 0 1px var(--color-acid-dim), 0 0 22px -10px var(--color-acid-glow)"
          : undefined,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}
